import { clsx } from 'clsx';

import { Stream, Streamable } from '@/vibes/soul/lib/streamable';
import { Image } from '~/components/image';
import { Link } from '~/components/link';

interface Props {
  className?: string;
  logo?: Streamable<string | { src: string; alt: string } | null>;
  label?: string;
  href: string;
  width: number;
  height: number;
}

// eslint-disable-next-line valid-jsdoc
/**
 * This component supports various CSS variables for theming. Here's a comprehensive list, along
 * with their default values:
 *
 * ```css
 * :root {
 *   --logo-focus: hsl(var(--primary));
 *   --logo-font-family: var(--font-family-heading);
 *   --logo-text: hsl(var(--foreground));
 * }
 * ```
 */
export function Logo({ className, logo: streamableLogo, href, width, height, label }: Props) {
  return (
    <Stream
      fallback={
        <div
          className="animate-pulse rounded-md bg-contrast-100"
          style={{ width: `${width}px`, height: `${height}px`, minWidth: `${width}px`, minHeight: `${height}px` }}
        />
      }
      value={streamableLogo}
    >
      {(logo) => (
        <Link
          aria-label={label}
          className={clsx(
            'relative flex sm:p-2 lg:p-0 items-center outline-0 ring-[var(--logo-focus,hsl(var(--primary)))] ring-offset-4 focus-visible:ring-2',
            className,
          )}
          href={href}
          style={typeof logo === 'string' ? {} : { maxWidth: `${width}px`, height: `${height}px` }}
        >
          {typeof logo === 'object' && logo !== null && logo.src !== '' ? (
            <Image
              alt={logo.alt}
              className="h-auto w-full object-contain object-left"
              height={height}
              src={logo.src}
              width={width}
            />
          ) : (
            typeof logo === 'string' && (
              <span className="font-[family-name:var(--logo-font-family,var(--font-family-heading))] text-lg font-semibold leading-none text-[var(--logo-text,hsl(var(--foreground)))] @xl:text-2xl">
                {logo}
              </span>
            )
          )}
        </Link>
      )}
    </Stream>
  );
}
